import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import useFetch from '../helpers/useFetch';

// The admin route the shop page's Done button comes back to carries the
// session it is returning from, which is what tells this hook to pick the trip
// up. It is a property of the route, not of any one tab or display mode — both
// of those can be different from what they were when the merchant left.
//
// While it is on the route it means one thing: there is a stored snapshot of
// the block that is still the newest version of it. It comes off when that
// stops being true, which happens in exactly two ways — the snapshot is
// applied to the form, or a save supersedes it. Read outcome is not the test:
// a read that failed applied nothing and superseded nothing, so the token
// stays and a reload retries.
export const PLACEMENT_RETURN_PARAM = 'rlv_placement';

const RESTORED_WITH_PLACEMENT = 'Restored your unsaved changes and applied the new position — press Save to keep it.';

const RESTORED_WITHOUT_PLACEMENT =
  'Restored your unsaved changes. No position was picked on your site, so it is unchanged.';

const RESTORED_STATUS_UNREADABLE =
  'Restored your unsaved changes. We could not read the position you picked — the position on this form is unchanged.';

// Expiry is one of the reasons a read fails and not the only one, so the copy
// names it as a possibility and points at the one action that helps: the token
// is still on the route, so reloading asks again.
const NOT_RESTORED =
  'That positioning session could not be read — it may have expired, or the request may have failed. ' +
  'Nothing on this form has been changed; reloading this page will try again.';

// Picks a positioning trip back up when the route carries
// `?rlv_placement=<token>` and hands back the block the form should open on:
// what was stored on the way out, with the position reported by the shop page
// laid over the top.
//
// This runs in the page rather than in the form, and hands over a value rather
// than pushing into the form's setters, for one reason: `pending` keeps the
// page on the spinner it already shows while its own read is outstanding, so
// the form is mounted exactly once, already holding the restored block. There
// is no window in which a merchant is looking at — typing into, or saving —
// a form that is about to be replaced underneath them.
//
// `blockPath` is the admin path of the block the session belongs to — a product
// block's or a static banner block's — under which its `placement-sessions`
// routes live. Nothing else about the block reaches this hook, and nothing
// here is specific to either kind.
const useRestorePlacementSession = ({ blockPath }) => {
  const fetch = useFetch();
  const location = useLocation();
  const navigate = useNavigate();
  const returnedToken = new URLSearchParams(location.search).get(PLACEMENT_RETURN_PARAM);
  // Set on the first render, before anything has been read, so the page never
  // gets one render in which it believes there is nothing to wait for.
  const [pending, setPending] = useState(Boolean(returnedToken));
  const [restored, setRestored] = useState(null);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);

  const clearReturnedToken = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete(PLACEMENT_RETURN_PARAM);
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  }, [location.pathname, location.search, navigate]);

  // `clearReturnedToken` is rebuilt on every `location.search` change —
  // including a tab switch that only adds or edits some other query
  // parameter and leaves the token untouched. The restore effect below reads
  // it through this ref instead of naming it as a dependency, so a tab
  // switch does not re-run that effect.
  const clearReturnedTokenRef = useRef(clearReturnedToken);
  useEffect(() => {
    clearReturnedTokenRef.current = clearReturnedToken;
  }, [clearReturnedToken]);

  // The token a read has already been resolved for — successfully or not.
  // A tab switch after a failed read leaves the same token on the route, so
  // without this the effect below would fire again on every such switch: on
  // a retry that then succeeds it would apply a stale snapshot and strip the
  // token out from under whatever the merchant has typed since, under a
  // false "restored" notice. Keyed on the token itself, not a boolean, so a
  // genuinely new token (a fresh trip) is still read.
  const handledTokenRef = useRef(null);

  useEffect(() => {
    if (!returnedToken || handledTokenRef.current === returnedToken) {
      return undefined;
    }

    let cancelled = false;
    const sessionPath = `${blockPath}/placement-sessions/${returnedToken}`;

    Promise.all([
      fetch(`${sessionPath}/state`),
      // A status that cannot be read is a worse-but-survivable outcome than
      // losing the form: tagged `unreadable` rather than folded into "nothing
      // was placed", which is a different, false claim.
      fetch(`${sessionPath}/status`).catch(() => ({ unreadable: true })),
    ])
      .then(([{ state }, status]) => {
        if (cancelled) {
          return;
        }

        // Resolved either way from here down: a retry after a tab switch
        // must not repeat any of this.
        handledTokenRef.current = returnedToken;

        // `useFetch` resolves an empty body to `{}`, so a session that answers
        // without a stored form arrives here rather than in the catch below.
        // Nothing to restore either way, so it is the same outcome as a read
        // that failed.
        if (!state) {
          setError(NOT_RESTORED);
          setPending(false);
          return;
        }

        if (status.placement?.cssSelector) {
          setRestored({
            ...state,
            cssSelector: status.placement.cssSelector,
            displayStrategy: status.placement.displayStrategy || state.displayStrategy,
          });
          setNotice(RESTORED_WITH_PLACEMENT);
        } else if (status.unreadable) {
          setRestored(state);
          setNotice(RESTORED_STATUS_UNREADABLE);
        } else {
          setRestored(state);
          setNotice(RESTORED_WITHOUT_PLACEMENT);
        }

        // Released before the strip: the strip navigates, which ends this run
        // of the effect.
        setPending(false);
        // Applied, so retired: the merchant is looking at the snapshot now,
        // and a reload must not lay it back over whatever they do next. Read
        // through the ref rather than the callback that made this run fire
        // again on a tab switch in the first place.
        clearReturnedTokenRef.current();
      })
      .catch(() => {
        if (!cancelled) {
          handledTokenRef.current = returnedToken;
          setError(NOT_RESTORED);
          setPending(false);
        }
      });

    // Stripping the token re-runs this effect; without the guard an unmount
    // between the reads going out and landing would still set state.
    return () => {
      cancelled = true;
    };
    // `clearReturnedToken` deliberately left out: it changes identity on
    // every `location.search` change (a tab switch included), and is read
    // through `clearReturnedTokenRef` instead so that can never re-run this
    // effect for a token it has already resolved.
  }, [fetch, blockPath, returnedToken]);

  // Puts the messages away without touching the token: closing the
  // unreadable-session alert is the merchant acknowledging it, not answering
  // it, so the retry the copy promises has to survive the close.
  const dismiss = useCallback(() => {
    setNotice(null);
    setError(null);
  }, []);

  // Superseded, so retired — the other way the token's meaning expires. The
  // case this exists for is a read that failed: the merchant redoes the work
  // by hand and saves, and without this the route still names the session, so
  // coming back to that entry — by reloading, which the failure message
  // suggests, or by Back to the entry a Save & Close pushed off — reads the
  // session successfully and lays the pre-trip snapshot back over the save,
  // under a notice telling them to press Save. Guarded on the token so an
  // ordinary save, on a route that never carried one, does not navigate.
  const onSaved = useCallback(() => {
    dismiss();
    if (returnedToken) {
      clearReturnedToken();
    }
  }, [clearReturnedToken, dismiss, returnedToken]);

  return { pending, restored, notice, error, dismiss, onSaved };
};

export default useRestorePlacementSession;
